"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useEnvContext } from "next-runtime-env";
import { parseAsInteger, useQueryState } from "nuqs";
import { useCallback } from "react";

import { getWishlistInfiniteProductsQueryOptions } from "../api/wishlist.query";

type Props = {
  enabled: boolean;
};

export function useInfiniteWishlistResults({ enabled }: Props) {
  const { NEXT_PUBLIC_BFF_API } = useEnvContext();
  const [, setPage] = useQueryState("page", parseAsInteger.withDefault(1));

  const { queryKey, queryFn } = getWishlistInfiniteProductsQueryOptions({
    apiOrigin: NEXT_PUBLIC_BFF_API!,
  });

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.total_pages
        ? lastPage.pagination.page + 1
        : undefined,
    enabled,
  });

  const lastPage = query.data?.pages.at(-1);
  const products = query.data?.pages.flatMap((p) => p.products) ?? [];
  const totalPages = lastPage?.pagination.total_pages ?? 0;
  const isLoading = query.isLoading || query.isPending;
  const isLastPage = !query.hasNextPage && !isLoading && totalPages > 0;

  const { fetchNextPage } = query;

  const loadMoreProducts = useCallback(async () => {
    const result = await fetchNextPage();
    const newPage = result.data?.pages.at(-1)?.pagination?.page;
    if (newPage !== undefined && newPage > 1) {
      setPage(newPage);
    }
  }, [fetchNextPage, setPage]);

  return {
    products,
    isLoading,
    isLastPage,
    totalPages,
    loadMoreProducts,
    isFetchingMore: query.isFetchingNextPage,
  };
}
